<template>
  <div id="app" :class="{'no-margin': $route.name === 'login'}">
    <el-dialog
      :show-close="false"
      :visible.sync="addVideoModalOpen"
      width="750px">
      <template slot="title">
        <div class="modal-title acr-bold-22">
          <div>
            <div class="modal-btn" @click="addVideoModalOpen = false">
              <svgicon name="Chevron-left"></svgicon>
            </div>
            <span style="margin-top: -1px">Add new videos</span>
          </div>
          <div class="modal-btn active" @click="confirmVideoUpload">
            <svgicon name="Arrow-right"></svgicon>
          </div>
        </div>
      </template>
      <div class="modal-videos">
        <div class="modal-video" v-for="(item, index) in files" :key="item.id">
          <div class="modal-video__title acr-bold-18">Video #{{index + 1}}</div>
          <input type="text" v-model="item.name" class="modal-video__input">
          <video :src="item.preview" controls class="modal-video__src"></video>
        </div>
      </div>
    </el-dialog>
    <el-dialog
      :visible.sync="createProjectModalOpen"
      title="Create new project"
      width="750px">
      <div class="modal-videos">
        <div class="modal-field">
          <div class="modal-field__label acr-bold-18">Project name</div>
          <el-input label="Project name" v-model="projectName"></el-input>
        </div>
        <div class="modal-field">
          <div class="modal-field__label acr-bold-18">Type of project</div>
          <el-select v-model="projectType" placeholder="Select">
            <el-option
              v-for="item in projectTypes"
              :key="item"
              :label="item"
              :value="item">
            </el-option>
          </el-select>
        </div>
        <div class="divider full-width" />
        <div class="modal-field" v-if="projectType === 'video_analytics'">
          <div class="modal-field__label acr-bold-18">Options</div>
          <div>
            <el-checkbox v-model="saveSound">Save sound</el-checkbox>
          </div>
          <div>
            <el-checkbox v-model="fastSceneCutting">Fast scenes cutting</el-checkbox>
          </div>
          <div>
            <el-checkbox v-model="detectPersons">Detect persons</el-checkbox>
          </div>
          <div>
            <el-checkbox v-model="detectSlowMotion">Detect slow motion</el-checkbox>
          </div>
        </div>
        <div class="modal-field" v-if="projectType === 'brand_detection'">
          <div class="modal-field__label acr-bold-18">Select model type</div>
          <model-type-select v-model="modelType" />
        </div>
        <div class="full-width" v-if="projectType === 'brand_detection' && modelType">
          <brand-list :list="brandList" />
        </div>
        <div class="divider full-width" v-if="projectType" />
        <div class="modal-field__label acr-bold-18 full-width">Selected video(s)</div>
        <div class="modal-video" v-for="(item) in selectedVideos" :key="item.id">
          <div class="modal-video__title acr-bold-18"></div>
          <video :src="item.original_video_url" :poster="item.thumbnail_url" controls class="modal-video__src"></video>
        </div>
      </div>
      <template slot="footer">
        <el-button @click="createProjectModalOpen = false">Cancel</el-button>
        <el-button
          type="primary"
          :disabled="!projectType || !projectName"
          @click="createProject"
        >
          Create project
        </el-button>
      </template>
    </el-dialog>
    <el-dialog
      :show-close="false"
      :visible.sync="createFolderModalOpen"
      width="515px">
      <template slot="title">
        <div class="modal-title acr-bold-22">
          <div>
            <div class="modal-btn" @click="createFolderModalOpen = false">
              <svgicon name="Chevron-left"></svgicon>
            </div>
            <span style="margin-top: -1px">Create new folder</span>
          </div>
          <div class="modal-btn" :class="{'active': newFolderName}" @click="createFolder">
            <svgicon name="Arrow-right"></svgicon>
          </div>
        </div>
      </template>
      <div class="modal-field">
        <div class="modal-field__label acr-bold-18">Folder name</div>
        <el-input label="Project name" v-model="newFolderName"></el-input>
      </div>
    </el-dialog>
    <el-dialog
      :show-close="false"
      :visible.sync="deleteVideosModal"
      width="788px">
      <template slot="title">
        <div class="modal-title acr-bold-22">
          <div>
            <div class="modal-btn" @click="deleteVideosModal = false">
              <svgicon name="Chevron-left"></svgicon>
            </div>
            <span style="margin-top: -1px">Are you sure you want to delete this video(s)?</span>
          </div>
          <div class="modal-btn active" @click="deleteSelected('video', fileToDelete)">
            <svgicon name="Arrow-right"></svgicon>
          </div>
        </div>
      </template>
      <div class="modal-videos" v-if="selectedVideos">
        <div class="modal-video" v-for="item in selectedVideos" :key="item.id">
          <div class="modal-video__title acr-bold-18"></div>
          <video :src="item.original_video_url" :poster="item.thumbnail_url" controls class="modal-video__src"></video>
        </div>
      </div>
    </el-dialog>
    <div class="main-wrapper" id="main" :key="appKey" :class="{'static': $route.name === 'login', 'condensed': files.length > 0 || creatingProject || selectedVideos.length > 0}">
    <router-view name="header" />
      <BaseLoader v-if="loadingComponent" />
      <!--<router-view name="aside"/>-->
      <router-view class="root-content" v-show="!loadingComponent"/>
    </div>
    <BaseFooter v-if="files.length > 0 && selectedVideos.length === 0" :active="allVideosUploaded" :on-confirm="openAddVideoModal">
      <UploadListFooter @deleteVideo="deleteVideo"></UploadListFooter>
    </BaseFooter>
    <BaseFooter v-if="(files.length === 0 && selectedVideos.length > 0) || creatingProject"
                :active="files.length === 0 && selectedVideos.length > 0"
                title="Selected videos"
                show-close
                :on-confirm="openCreateProjectModal"
                @close="clearSelected"
                action-btn-text="Create project">
      <template slot="options" v-if="(files.length === 0 && selectedVideos.length > 0)">
        <div class="footer-btn transparent"><span style="margin-top: -3px">Selected: {{selectedVideos.length}}</span></div>
        <div class="footer-btn" @click="createFolderModalOpen = true"><span style="margin-top: -3px">Create folder</span></div>
        <div class="footer-btn" @click="deleteVideosModal = true"><span style="margin-top: -3px">Delete</span></div>
      </template>
      <SelectedVideosFooter></SelectedVideosFooter>
    </BaseFooter>
    <portal-target name="app"></portal-target>
    <!--<router-view name="footer"/>-->
  </div>
</template>

<script>
import BaseFooter from './components/BaseFooter';
import BaseLoader from './components/BaseLoader';
import BrandList from './components/BrandList';
import ModelTypeSelect from './components/ModelTypeSelect';
import UploadListFooter from './components/footer/UploadListFooter';
import SelectedVideosFooter from './components/footer/SelectedVideosFooter';
import { createProject } from "./api/projects";
import { createFolder } from "./api/folders";
import { updateVideo } from "./api/files";
import { getModel } from "./api/models";
import { mapGetters, mapActions } from 'vuex';
export default {
  name: 'app',
  components: { BaseFooter, BrandList, ModelTypeSelect, UploadListFooter, SelectedVideosFooter, BaseLoader },
  data() {
    return {
      addVideoModalOpen: false,
      brandList: [],
      createProjectModalOpen: false,
      createFolderModalOpen: false,
      deleteVideosModal: false,
      appKey: 0,
      projectName: '',
      newFolderName: '',
      projectType: null,
      loadingComponent:true,
      saveSound: true,
      fastSceneCutting: false,
      detectSlowMotion: true,
      detectPersons: true,
      modelType: null,
    }
  },
  watch: {
    $route: {
      handler (to, from) {
        const body = document.getElementsByTagName('body')[0];
        if (from !== undefined) {
          body.classList.remove('page--' + from.name.toLowerCase());
        }
        body.classList.add('page--' + to.name.toLowerCase());
      },
      immediate: true,
    },
    modelType(value) {
      const model = this.brandDetectionModels.find(({ model_name }) => model_name === value) || {};
      if (model.id !== undefined) {
        this.getModelInfo(model.id);
      }
    },
  },
  computed: {
    ...mapGetters({
      files: 'uploadFiles',
      selectedVideos: 'selectedVideos',
      currentFolderId: 'currentFolderId',
      // loading: 'loading',
      searchQuery: 'searchQuery',
      searchResults: 'searchResults',
      isAuthenticated: 'isAuthenticated',
      projectTypes: 'projectTypes',
      brandDetectionModels: 'brandDetectionModels',
    }),
    ...mapActions({
      fetchProjectTypes: 'fetchProjectTypes',
      fetchAllBrandDetectionModels: 'fetchAllBrandDetectionModels',
    }),
    allVideosUploaded() {
      return !this.files.filter((v) => v.status !== 'uploaded').length > 0
    },
    creatingProject() {
      return this.$route.params.creatingProject;
    },
  },
  mounted() {
    if (this.isAuthenticated) {
      this.$store.dispatch('fetchProjectTypes');
      this.$store.dispatch('fetchAllBrandDetectionModels')
    }
    setTimeout(() => {
      this.loadingComponent = false
    }, 0)
  },
  methods: {
    forceRerender() {
      this.appKey += 1;
    },
    deleteVideo(item) {
      this.$store.dispatch('deleteVideo', item.videoId);
      this.$store.commit('UPLOAD_REMOVE', item.uid);
    },
    async getModelInfo(id) {
      const { data = {} } = await getModel(id);
      this.brandList = data.brands;
    },
    openAddVideoModal() {
      this.addVideoModalOpen = true;
    },
    openCreateProjectModal() {
      this.createProjectModalOpen = true;
    },
    clearSelected() {
      if (this.$route.params.creatingProject) {
        this.$router.push({ name: 'projects' })
      }
      this.$store.dispatch('clearSelectedVideos');
      this.forceRerender();
    },
    deleteSelected() {
      const promiseArr = [];

      this.selectedVideos.forEach((v) => {
        promiseArr.push(this.$store.dispatch('deleteVideo', v.id));
        this.$store.dispatch('removeSelectedVideo', v);
      });

      Promise.all(promiseArr).then(() => {
        this.deleteVideosModal = false;
        this.forceRerender()
      });
    },
    confirmVideoUpload() {

      const promiseArr = [];

      this.files.forEach(f => {
        const formData = new FormData();
        const payload = {
          display_name: f.name,
        };

        Object.keys(payload).forEach(key => formData.append(key, payload[key]));

        console.log(formData);

        promiseArr.push(updateVideo(formData, f.videoId));
      });

      Promise.all(promiseArr).then(() => {
        if (this.$route.name === 'dashboard') {
          this.$eventBus.$emit('refreshFiles');
        } else {
          this.$router.push({ name: 'dashboard' });
        }
        this.$store.dispatch('clearUpload');
        this.addVideoModalOpen = false;
      })
    },
    createFolder() {
      if (!this.newFolderName) return;

      let formD = new FormData();

      formD.append('name', this.newFolderName);
      formD.append('parent_id', this.currentFolderId);

      createFolder(formD).then((res) => {
        this.newFolderName = '';

        const promiseArr = [];

        this.selectedVideos.forEach((v) => {
          const formData = new FormData();
          formData.append('parent_id', res.data.msg.replace(/[^0-9]/g,''));

          promiseArr.push(updateVideo(formData, v.id));
        });

        Promise.all(promiseArr).then(() => {
          this.createFolderModalOpen = false;
          this.$store.dispatch('clearSelectedVideos');
          this.forceRerender()
        });
      });
    },
    createProject() {

      if (this.projectName && this.projectType) {
        const videosToAnalyse = [...this.selectedVideos.map(item => item.id)];

        let formD = new FormData();

        formD.append('project_name', this.projectName);
        formD.append('project_type', this.projectType);
        formD.append('video_ids', videosToAnalyse.join(','));

        if (this.projectType === 'video_analytics') {
          formD.append('save_sound', this.saveSound);
          formD.append('fast_scene_cutting', this.fastSceneCutting);
          formD.append('detect_slowmotion', this.detectSlowMotion);
          formD.append('detect_persons', this.detectPersons);
        } else {
          formD.append('model_type', this.modelType);
        }

        this.projectType = null;

        createProject(formD).then(() => {
          this.$store.dispatch('clearSelectedVideos');
          this.createProjectModalOpen = false;
          this.$router.push({name: 'projects'});
        }).catch(e => console.log(e));
      }
    },
  },
}
</script>
<style lang="scss" scoped>
  .no-margin {
    margin: 0 !important;
  }
  /deep/
  .el-select {
    width: 100%;
  }

  /deep/
  .el-dialog {
    .el-checkbox {
      &.is-checked {
        .el-checkbox__inner {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          &::after {
            width: 14px;
            height: 11px;
            content: url("./assets/svg/Done.svg");
            transform: scale(1) translate(-50%, -50%);
            border: none;
            left: 50%;
            top: 50%;
          }
        }
      }
      &__inner {
        width: 30px;
        height: 30px;
        border: none;
        background: rgba(36, 49, 67, 0.1);
        &::after {
          width: 14px;
          height: 11px;
          content: url("./assets/svg/Done.svg");
          transform: scale(0);
          transition: all .3s ease;
          border: none;
        }
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: all .2s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */ {
    transform: scale(0.98);
  }

  .full-width {
    grid-column: 1 / -1;
  }

  .divider {
    border-bottom: 1px solid var(--border-color);
    margin: 10px 0 5px;
  }
</style>
